import React, { useState, useEffect, useMemo } from "react";
import StudentEdit from "./StudentEdit";
import "./StudentsList.css";

const API_ROOT       = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const SPECIAL_EMPIDS = [1, 147, 27, 227];
const DOC_BASE       = "https://pumis.puchd.ac.in/uploads/student_profile_pic/";

function formatDOB(val) {
  if (!val) return "";
  const d = new Date(val);
  if (isNaN(d)) return String(val).slice(0, 10);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).replace(/ /g, "-");
}

export default function StudentsList() {
  const localUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); }
    catch { return null; }
  }, []);

  const moduleId  = parseInt(localUser?.moduleId, 10);
  const empId     = parseInt(localUser?.id,       10);
  const deptId    = parseInt(localUser?.deptid,   10);
  const isSpecial = SPECIAL_EMPIDS.includes(empId);

  const [departments,   setDepartments]   = useState([]);
  const [selectedDept,  setSelectedDept]  = useState(isSpecial ? 1000 : deptId);
  const [students,      setStudents]      = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState("");
  const [editId,        setEditId]        = useState(null);

  useEffect(() => {
    if (moduleId !== 5) return;
    fetch(`${API_ROOT}/api/students/departments?moduleId=${moduleId}`)
      .then(r => r.json())
      .then(data => Array.isArray(data) && setDepartments(data))
      .catch(() => {});
  }, [moduleId]);

  useEffect(() => {
    if (moduleId !== 5) return;
    fetchStudents(isSpecial ? 1000 : deptId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStudents = async (deptFilter) => {
    setError("");
    setLoading(true);
    try {
      const params = new URLSearchParams({
        moduleId,
        empId,
        deptId,
        selectedDepartmentID: deptFilter,
      });
      const res  = await fetch(`${API_ROOT}/api/students/student-list?${params}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to fetch students.");
      setStudents(data.data || []);
    } catch (err) {
      setError(err.message || "Failed to fetch students.");
    } finally {
      setLoading(false);
    }
  };

  const onView = (e) => {
    e.preventDefault();
    fetchStudents(selectedDept);
  };

  if (moduleId !== 5) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">Unauthorized access Or Session Timed out.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-3">
      <h3 className="ctitle">Students List</h3>

      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={onView} className="row g-2 align-items-end mb-3">
        <div className="col-auto">
          <label className="form-label mb-1">Department</label>
          <select
            className="form-select stu-dept-select"
            value={selectedDept}
            onChange={e => setSelectedDept(parseInt(e.target.value, 10))}
          >
            <option value={1000}>Display All Departments</option>
            {departments.map(d => (
              <option key={d.DepartmentID} value={d.DepartmentID}>{d.Department}</option>
            ))}
          </select>
        </div>
        <div className="col-auto">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Loading..." : "View"}
          </button>
        </div>
      </form>

      {!loading && students.length === 0 && !error && (
        <div className="alert alert-info">No students found.</div>
      )}

      {students.length > 0 && (
        <div className="table-responsive">
          <table className="pending-verif-table table table-sm table-bordered stu-list-table">
            <thead style={{ '--bs-table-bg': '#008080', color: 'white' }}>
              <tr>
                <th>SNo</th>
                <th>StuID</th>
                <th>Name</th>
                <th>Father's Name</th>
                <th>Mobile</th>
                <th>DOB</th>
                <th>PUPIN</th>
                <th>Course</th>
                <th>Department</th>
                <th>Address</th>
                <th>City</th>
                <th>State</th>
                <th>Active</th>
                <th>VehicleNo</th>
                <th>StickerNo</th>
                <th>Doc</th>
                <th>View</th>
              </tr>
            </thead>
            <tbody>
              {students.map((stu, idx) => (
                <tr key={stu.StudentID}>
                  <td>{idx + 1}</td>
                  <td>{stu.StudentID}</td>
                  <td className="stu-name-col">{stu.StudentName}</td>
                  <td>{stu.FatherName}</td>
                  <td>{stu.Mobile}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{formatDOB(stu.DOB)}</td>
                  <td>{stu.PUPIN}</td>
                  <td>{stu.CourseAbb}</td>
                  <td>{stu.Department}</td>
                  <td className="stu-addr-col">{stu.PermanentAddress}</td>
                  <td>{stu.City}</td>
                  <td>{stu.State}</td>
                  <td>{stu.Active}</td>
                  <td>{stu.VehicleNo}</td>
                  <td>{stu.StickerNo}</td>
                  <td>
                    {stu.ProfilePic ? (
                      <a
                        href={`${DOC_BASE}${stu.ProfilePic}`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-sm btn-outline-secondary"
                      >
                        View
                      </a>
                    ) : "-"}
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => setEditId(stu.StudentID)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editId && (
        <StudentEdit
          studentId={editId}
          moduleId={moduleId}
          onClose={() => setEditId(null)}
          onSaved={() => { setEditId(null); fetchStudents(selectedDept); }}
        />
      )}
    </div>
  );
}
